// TODO (Noah, 2024-10-09): Re-enable this rule
/* eslint-disable replo/consistent-component-exports */
import type { ExperimentStatus } from "schemas/experiment";
import type { ReploElementForExperiments } from "schemas/generated/element";
import type { Experiment } from "schemas/generated/experiment";
import type { FileWithLatestFileVersion } from "schemas/generated/file";
import type { Variation } from "schemas/generated/variation";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import { useAnalyticsOnboardingOAuthLink } from "@v1/features/analytics/useAnalyticsOnboardingOAuthLink";
import { DetailsContainer } from "@v1/features/experiments/components/DetailsContainer";
import { PageUrlSelect } from "@v1/features/experiments/components/PageUrlSelect";
import { LinkSection } from "@v1/features/experiments/components/sections/LinkSection";
import { useExperimentEdit } from "@v1/features/experiments/tabs/hooks/useExperimentEdit";
import { useExperimentApi } from "@v1/features/experiments/utils";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useDefaultCustomDomain from "@v2/features/page-settings/useDefaultCustomDomain";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { nextJSProjectRouter } from "@v2/trpc/routes/nextJSProject";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { Equal, EqualNot, ExternalLink, Info, Plus, Trash } from "lucide-react";
import { generatePath } from "react-router-dom";
import { isValidHttpUrl } from "replo-utils/lib/url";
import {
  getExperimentStatus,
  VARIATION_ERROR_MESSAGES,
} from "schemas/experiment";
import { isPathSafeSlug } from "schemas/utils";

import { VariationNameStartEnhancer } from "../components/VariationNameStartEnhancer";

export const ExperimentsEditTabV2: React.FC<{
  experiment: Experiment;
}> = ({ experiment }) => {
  const workspaceId = useCurrentWorkspaceId() ?? undefined;

  if (!workspaceId) {
    return <Loader />;
  }

  return (
    <>
      <ExperimentEditComponent
        workspaceId={workspaceId}
        experiment={experiment}
      />
    </>
  );
};

type SplitMethod = "equal" | "manual";

const ExperimentEditComponent = ({
  workspaceId,
  experiment,
}: {
  workspaceId: string;
  experiment: Experiment;
}) => {
  const { watch, dispatchExperimentEdit } = useExperimentEdit();
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const defaultCustomDomain = useDefaultCustomDomain();

  const {
    links: { data: links, isLoading: isLoadingLinks },
  } = useExperimentApi();

  const { data: allReploElements, isLoading: isLoadingAllReploElements } =
    workspaceRouter.getAllElementsWithShopifyUrl.useQuery(
      workspaceId || projectId ? { workspaceId, projectId } : skipToken,
    );

  // Fetch V2 pages if project uses Replo hosting
  const { data: v2PagesData, isLoading: isLoadingV2Pages } =
    nextJSProjectRouter.listPages.useInfiniteQuery(
      projectId && project?.hostingType === "replo"
        ? {
            projectId,
            limit: 100,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );

  // Flatten all V2 pages and filter for published pages only
  const publishedV2Pages = React.useMemo(() => {
    return (
      v2PagesData?.pages
        ?.flatMap((page) => page.pages)
        .filter((file) => {
          return (
            file.fileType === "page" &&
            file.lastPublishedFileVersionId !== null &&
            file.pageRoute !== null
          );
        }) ?? []
    );
  }, [v2PagesData]);

  const [splitMethod, setSplitMethod] = React.useState<SplitMethod>("equal");

  const { errors } = useExperimentEdit();

  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier || "free";

  const shouldForceCreateNewLink = links !== undefined && links.length === 0;

  const [linkSubSection, setLinkSubSection] = React.useState<
    "chooseLink" | "createNewLink"
  >(shouldForceCreateNewLink ? "createNewLink" : "chooseLink");

  const handleLinkSubsectionChange = (value: string) => {
    setLinkSubSection(value as "chooseLink" | "createNewLink");
    if (value === "createNewLink") {
      logEvent("experiment.link.create", {
        billingPlanTier: subscriptionTier,
      });
    }
  };

  const status = getExperimentStatus(experiment);
  const isEditable = status === "draft";

  const isAllocationPercentValid =
    errors.variations?.message !== VARIATION_ERROR_MESSAGES.allocationPercent;

  return (
    <div className="flex flex-col gap-2 pb-32">
      <div className="space-y-4 text-sm">
        {!isLoadingLinks && (
          <LinkSection
            linkSubSection={linkSubSection}
            handleLinkSubsectionChange={handleLinkSubsectionChange}
            isEditable={isEditable}
            links={links ?? []}
            shouldForceCreateNewLink={shouldForceCreateNewLink}
            experiment={{
              analyticsLinkId:
                watch("completedAnalyticsLinkId") ?? watch("analyticsLinkId"),
              name: experiment.name,
            }}
          />
        )}
        <DetailsContainer
          title="Variants"
          headerComponent={
            isEditable && (
              <div className="max-w-[55px]">
                <DeprecatedToggleGroup
                  size="sm"
                  options={[
                    {
                      label: <EqualNot size={12} />,
                      value: "manual",
                      tooltipContent: "Manual split",
                    },
                    {
                      label: <Equal size={12} />,
                      value: "equal",
                      tooltipContent: "Equal split",
                    },
                  ]}
                  selectedValue={splitMethod}
                  onChange={(value) => {
                    setSplitMethod(value as SplitMethod);
                    if (value === "equal") {
                      dispatchExperimentEdit({
                        type: "setEqualVariationSplit",
                      });
                    }
                  }}
                />
              </div>
            )
          }
          isRequired
        >
          <div className="space-y-3">
            {watch("variations").map((variation, index) => (
              <VariationEntry
                key={variation.id}
                index={index}
                {...variation}
                isAllocationPercentValid={isAllocationPercentValid}
                splitMethod={splitMethod}
                allowRemoval={watch("variations").length > 1}
                status={status}
                allReploElements={allReploElements?.elements ?? []}
                isLoadingAllReploElements={isLoadingAllReploElements}
                publishedV2Pages={publishedV2Pages}
                isLoadingV2Pages={isLoadingV2Pages}
                project={project}
                defaultCustomDomain={defaultCustomDomain?.value ?? null}
              />
            ))}
            <div
              className={twMerge(
                "text-rose",
                !errors.variations?.message && "hidden",
              )}
            >
              {errors.variations?.message}
            </div>
            {isEditable && (
              <div
                className="bg-sheet hover:bg-surface border-0.5 border-border flex cursor-pointer items-center justify-center rounded-sm border-dashed py-4"
                onClick={() => dispatchExperimentEdit({ type: "addVariation" })}
              >
                <div className="flex flex-row items-center gap-1">
                  <Plus size={16} />
                  <span className="text-xs font-medium">Add variant</span>
                </div>
              </div>
            )}
          </div>
        </DetailsContainer>
        <DetailsContainer title="Description">
          <div className="border-0.5 border-border flex rounded-sm p-4">
            <DeprecatedTextarea
              layoutClassName="w-full"
              placeholder="Keep track of why you are testing or other test details, for yourself or others on your team"
              onChange={(description) => {
                return dispatchExperimentEdit({
                  type: "changeProperty",
                  payload: { key: "description", value: description },
                });
              }}
              value={watch("description") ?? ""}
              size="sm"
            />
          </div>
        </DetailsContainer>
      </div>
    </div>
  );
};

const EnableAnalyticsButton: React.FC<{ shopifyUrl: string }> = ({
  shopifyUrl,
}) => {
  const { oauthLink, isLoading } = useAnalyticsOnboardingOAuthLink(shopifyUrl);
  const productAnalytics = useLogAnalytics();

  return (
    <div className="text-primary">
      <DeprecatedButton
        size="sm"
        variant="inherit"
        to={oauthLink ?? ""}
        isLoading={isLoading || !oauthLink}
        onClick={() => {
          return productAnalytics("analytics.connect", {
            tab: "experiment_details_tab",
          });
        }}
      >
        Enable Analytics
      </DeprecatedButton>
    </div>
  );
};

const ConnectShopifyVariationEntryInfoBanner: React.FC = () => {
  return (
    <div className="bg-sky-soft flex flex-row items-center justify-between rounded-md px-2 py-1">
      <div className="text-primary flex flex-row gap-3">
        <Info size={14} />
        <span className="text-xs">
          This page must be part of a Replo project to track results.
        </span>
      </div>
    </div>
  );
};

const ConnectAnalyticsVariationEntryInfoBanner: React.FC<{
  shopifyUrl: string;
}> = ({ shopifyUrl }) => {
  return (
    <div className="bg-sky-soft flex flex-row items-center justify-between rounded-md px-2 py-1">
      <div className="text-primary flex flex-row gap-3">
        <Info size={14} />
        <span className="text-xs">
          You must enable Analytics for this Shopify integration in order to
          track results on this page.
        </span>
      </div>
      <EnableAnalyticsButton shopifyUrl={shopifyUrl} />
    </div>
  );
};

function convertElementToFullUrl(element: ReploElementForExperiments) {
  return `https://${element.shopifyUrl}/pages/${element.shopifyPagePath}`;
}

function convertFileToFullUrl(
  file: FileWithLatestFileVersion,
  shopDomainPrefix: string | null | undefined,
  customDomain: string | null,
): string {
  let domain: string | null = null;
  if (customDomain) {
    domain = customDomain;
  } else if (shopDomainPrefix) {
    domain = `${shopDomainPrefix}.reploshops.com`;
  }

  return `https://${domain}${file.pageRoute}`;
}

const VariationEntryField: React.FC<
  React.PropsWithChildren<{
    title: string | null;
    className?: string;
  }>
> = ({ title, children, className }) => {
  return (
    <div className={twMerge("flex flex-col gap-1.5", className)}>
      {title && <span className="text-xs font-medium">{title}</span>}
      <div>{children}</div>
    </div>
  );
};

const VariationEntry = ({
  allowRemoval,
  isAllocationPercentValid,
  status,
  allReploElements,
  isLoadingAllReploElements,
  publishedV2Pages,
  isLoadingV2Pages,
  project,
  defaultCustomDomain,
  splitMethod,
  index,
  ...variation
}: Variation & {
  allowRemoval: boolean;
  isAllocationPercentValid: boolean;
  status: ExperimentStatus;
  allReploElements: ReploElementForExperiments[];
  isLoadingAllReploElements: boolean;
  publishedV2Pages: FileWithLatestFileVersion[];
  isLoadingV2Pages: boolean;
  project:
    | {
        id: string;
        shopDomainPrefix?: string | null;
        hostingType: "replo" | "shopify";
      }
    | null
    | undefined;
  defaultCustomDomain: string | null;
  splitMethod: SplitMethod;
  index: number;
}) => {
  const isEditable = status === "draft";

  const { dispatchExperimentEdit } = useExperimentEdit();

  const allPageOptions = React.useMemo(() => {
    const v1Options = allReploElements.map((element) => {
      const fullUrl = convertElementToFullUrl(element);
      return {
        label: fullUrl,
        value: fullUrl,
      };
    });

    const v2Options = publishedV2Pages
      .map((file) => {
        const fullUrl = convertFileToFullUrl(
          file,
          project?.shopDomainPrefix ?? null,
          defaultCustomDomain,
        );
        return {
          label: fullUrl,
          value: fullUrl,
        };
      })
      .filter(
        (option): option is { label: string; value: string } => option !== null,
      );

    return [...v1Options, ...v2Options];
  }, [
    allReploElements,
    publishedV2Pages,
    project?.shopDomainPrefix,
    defaultCustomDomain,
  ]);

  const target = variation.target;

  let isTargetConnectedToShopify = false;
  let isTargetStoreConnectedToAnalytics = false;

  let matchingElement: ReploElementForExperiments | undefined;

  if (target && isValidHttpUrl(target)) {
    const targetDomain = new URL(target).hostname;

    matchingElement = allReploElements.find(
      (element) => element.shopifyUrl === targetDomain,
    );

    isTargetConnectedToShopify = Boolean(matchingElement);
    isTargetStoreConnectedToAnalytics = Boolean(matchingElement?.hasWebPixelId);
  }

  const targetElement = allReploElements.find(
    (element) => convertElementToFullUrl(element) === target,
  );

  const targetV2File = publishedV2Pages.find((file) => {
    const fileUrl = convertFileToFullUrl(
      file,
      project?.shopDomainPrefix ?? null,
      defaultCustomDomain,
    );
    return fileUrl === target;
  });

  const handlePageLinkIconClick = () => {
    if (targetElement) {
      window.open(
        generatePath(routes.editor.element, {
          projectId: targetElement.projectId,
          elementId: targetElement.id,
        }),
        "_blank",
      );
    } else if (targetV2File && project?.id) {
      window.open(
        generatePath(routes.shops.home, {
          projectId: project.id,
        }),
        "_blank",
      );
    }
  };

  const shopifyUrl = targetElement?.shopifyUrl;
  const shouldShowShopifyBanner =
    target && !isTargetConnectedToShopify && !targetV2File;
  // NOTE (Max, 2024-11-14): We only wanna show the analyticsBanner if the
  // target is connected to Shopify. V2 pages don't need this banner.
  const shouldShowAnalyticsBanner =
    target &&
    shopifyUrl &&
    isTargetConnectedToShopify &&
    !isTargetStoreConnectedToAnalytics &&
    !targetV2File;

  const handleSelectedPageUrlChange = (value: string) => {
    dispatchExperimentEdit({
      type: "changeVariation",
      payload: { ...variation, target: value },
    });
  };

  return (
    <div className="border-0.5 border-border flex flex-col gap-2 rounded-sm p-4">
      <div className="flex w-full gap-3">
        <VariationEntryField title="Name" className="max-w-[178px] flex-none">
          {isEditable ? (
            <DeprecatedInput
              startEnhancer={<VariationNameStartEnhancer index={index} />}
              size="sm"
              value={variation.slug}
              type="text"
              layoutClassName="h-8"
              unsafe_inputClassName="text-xs"
              validityState={
                !isPathSafeSlug(variation.slug) ? "invalid" : "valid"
              }
              onChange={(e) => {
                return dispatchExperimentEdit({
                  type: "changeVariation",
                  payload: { ...variation, slug: e.currentTarget.value },
                });
              }}
            />
          ) : (
            <div className="min-w-[120px] py-1 pr-2">{variation.slug}</div>
          )}
        </VariationEntryField>
        <VariationEntryField title="Page" className="min-w-0 flex-1">
          <PageUrlSelect
            isEditable={isEditable}
            target={target}
            onChange={handleSelectedPageUrlChange}
            options={allPageOptions}
          />
        </VariationEntryField>
        <div className="border-0.5 border-border" />
        <VariationEntryField title="Split" className="max-w-[55px] flex-none">
          {isEditable ? (
            <DeprecatedInput
              size="base"
              value={variation.allocationPercent.toString()}
              validityState={!isAllocationPercentValid ? "invalid" : "valid"}
              type="number"
              unsafe_inputClassName="text-xs"
              isDisabled={splitMethod === "equal"}
              onChange={(e) => {
                const percent = Number.parseInt(e.currentTarget.value);
                const allocationPercent = Number.isNaN(percent)
                  ? 0
                  : Math.max(percent, 0);
                dispatchExperimentEdit({
                  type: "changeVariation",
                  payload: { ...variation, allocationPercent },
                });
              }}
            />
          ) : (
            <div className="py-1">{variation.allocationPercent}%</div>
          )}
        </VariationEntryField>
        <VariationEntryField title={null} className="flex-none justify-center">
          <div className="flex flex-col items-center justify-center gap-2">
            <DeprecatedIconButton
              variant="tertiary"
              size="sm"
              disabled={!targetElement && !targetV2File}
              onClick={handlePageLinkIconClick}
              icon={<ExternalLink size={16} />}
            />
            {isEditable && (
              <DeprecatedIconButton
                variant="tertiary"
                size="sm"
                disabled={!allowRemoval}
                onClick={() => {
                  return dispatchExperimentEdit({
                    type: "removeVariation",
                    payload: variation.id,
                  });
                }}
                icon={<Trash size={16} />}
              />
            )}
          </div>
        </VariationEntryField>
      </div>
      {shouldShowShopifyBanner && (
        <div>
          <ConnectShopifyVariationEntryInfoBanner />
        </div>
      )}
      {shouldShowAnalyticsBanner && (
        <div>
          <ConnectAnalyticsVariationEntryInfoBanner shopifyUrl={shopifyUrl} />
        </div>
      )}
    </div>
  );
};
